// Port of ios/Sources/ViewModels/ChannelNotificationPreferences.swift.
//
// Mattermost keeps a channel's mobile notification level and its unread/mute policy as separate
// properties. Keeping that split here prevents a mute toggle from silently rewriting which push
// notifications the reader asked for, or a push-level edit from unmuting the channel.

export type PushLevel = 'default' | 'all' | 'mention' | 'none';

export const pushLevels: readonly PushLevel[] = ['default', 'all', 'mention', 'none'];

export interface State {
  pushLevel: PushLevel;
  isMuted: boolean;
}

export function derive(push: string | null | undefined, markUnread: string | null | undefined): State {
  return {
    pushLevel: (pushLevels as readonly string[]).includes(push ?? '') ? (push as PushLevel) : 'default',
    isMuted: markUnread === 'mention',
  };
}

export function notifyProps(state: State): Record<string, string> {
  return {
    push: state.pushLevel,
    mark_unread: state.isMuted ? 'mention' : 'all',
  };
}

/**
 * WEB ADDITION (shared/API.md §8.3.2: "the server updates only the fields provided"): the keys
 * that differ between two states — one switch flipped sends ONE key, so a concurrent change to
 * the other property elsewhere is never overwritten with the value this page loaded. Empty when
 * nothing changed. The Swift sheet sends both owned keys every time; the quick mute already
 * sends only `mark_unread` (ChannelMute.notifyProps), and this is the same rule for both halves.
 */
export function changedProps(previous: State, next: State): Record<string, string> {
  const all = notifyProps(next);
  const out: Record<string, string> = {};
  if (previous.pushLevel !== next.pushLevel) out.push = all.push;
  if (previous.isMuted !== next.isMuted) out.mark_unread = all.mark_unread;
  return out;
}

/** Namespace form, for `ChannelNotificationPreferences.derive(...)` call sites. */
export const ChannelNotificationPreferences = { pushLevels, derive, notifyProps, changedProps } as const;
